import React, { useState, useEffect, useCallback } from "react";
import Header from "../components/Header";
import Toast from "../components/Toast";
import { offerService } from "../services/apiService";

/**
 * Client-facing WhatsApp Cloud API connection page.
 * Uses Meta Embedded Signup (Facebook JS SDK) so each client links their own
 * WhatsApp Business Account (WABA). App-level credentials come from the backend
 * config response; when the feature is not enabled, a clear notice is shown.
 */
const WhatsAppConnect = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [toast, setToast] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateLang, setTemplateLang] = useState("en");

  const showMsg = (type, text) => setToast({ type, message: text });

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await offerService.getWhatsAppConfig();
      setConfig(data);
      setTemplateName(data.templateName || "");
      setTemplateLang(data.templateLanguage || "en");
    } catch {
      showMsg("error", "Failed to load WhatsApp status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  // Load the Facebook JS SDK once, only if the feature is enabled and app id set.
  useEffect(() => {
    if (!config?.featureEnabled || !config?.appId) return;
    if (window.FB) { setSdkReady(true); return; }
    window.fbAsyncInit = function () {
      window.FB.init({ appId: config.appId, autoLogAppEvents: true, xfbml: true, version: "v21.0" });
      setSdkReady(true);
    };
    const s = document.createElement("script");
    s.async = true; s.defer = true; s.crossOrigin = "anonymous";
    s.src = "https://connect.facebook.net/en_US/sdk.js";
    document.body.appendChild(s);
  }, [config]);

  // Launch Embedded Signup. On success, Meta returns a code; the WABA/phone id
  // arrive via the message event. Sent to the backend to finalize + store.
  const launchSignup = () => {
    if (!window.FB || !config?.configId) {
      showMsg("error", "WhatsApp signup is not available yet.");
      return;
    }
    let captured = { wabaId: "", phoneNumberId: "" };
    const onMessage = (event) => {
      if (!String(event.origin).endsWith("facebook.com")) return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === "WA_EMBEDDED_SIGNUP" && data.data) {
          captured.wabaId = data.data.waba_id || "";
          captured.phoneNumberId = data.data.phone_number_id || "";
        }
      } catch { /* non-JSON messages ignored */ }
    };
    window.addEventListener("message", onMessage);

    window.FB.login(
      async (response) => {
        window.removeEventListener("message", onMessage);
        const code = response?.authResponse?.code;
        if (!code) {
          showMsg("error", "WhatsApp connection cancelled.");
          return;
        }
        setWorking(true);
        try {
          await offerService.connectWhatsApp({
            code,
            wabaId: captured.wabaId,
            phoneNumberId: captured.phoneNumberId,
          });
          showMsg("success", "WhatsApp connected successfully.");
          loadConfig();
        } catch (e) {
          showMsg("error", e?.response?.data?.message || "Failed to connect WhatsApp.");
        } finally {
          setWorking(false);
        }
      },
      {
        config_id: config.configId,
        response_type: "code",
        override_default_response_type: true,
        extras: { setup: {} },
      },
    );
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) { showMsg("error", "Enter the approved template name"); return; }
    setWorking(true);
    try {
      await offerService.setWhatsAppTemplate({ templateName: templateName.trim(), language: templateLang });
      showMsg("success", "Template saved.");
      loadConfig();
    } catch { showMsg("error", "Failed to save template"); }
    finally { setWorking(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect WhatsApp for this account?")) return;
    setWorking(true);
    try {
      await offerService.disconnectWhatsApp();
      showMsg("success", "WhatsApp disconnected.");
      loadConfig();
    } catch { showMsg("error", "Failed to disconnect"); }
    finally { setWorking(false); }
  };

  // Small status pill shown in the card header.
  const StatusBadge = () => {
    if (loading) return null;
    if (!config?.featureEnabled) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
          <i className="fas fa-circle text-[7px]" /> Not enabled
        </span>
      );
    }
    if (config?.connected) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">
          <i className="fas fa-circle text-[7px]" /> Connected
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-500">
        <i className="fas fa-circle text-[7px]" /> Not connected
      </span>
    );
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-4">
      <Header showSearch={false} showCart={false} />
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      <div className="flex-1 min-h-0 mt-3 flex">
        <div className="w-full flex">
          <section className="flex-1 flex flex-col rounded-xl overflow-hidden shadow-md border border-blue-200 bg-white">
            {/* Card header — jashbill gradient bar */}
            <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
              <h2 className="flex items-center gap-2 text-base font-bold text-blue-800">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-green-500 text-white shadow-sm">
                  <i className="fab fa-whatsapp" />
                </span>
                Connect WhatsApp
              </h2>
              <StatusBadge />
            </div>

            <div className="p-5 space-y-4 flex-1 min-h-0 overflow-auto">
              {loading ? (
                <div className="py-10 text-center"><i className="fas fa-spinner fa-spin text-blue-400 text-2xl" /></div>
              ) : !config?.featureEnabled ? (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-700">
                  <i className="fas fa-info-circle mr-2" />
                  WhatsApp API is not enabled yet. Please contact support to activate it for your account.
                </div>
              ) : config?.connected ? (
                <>
                  <div className="rounded-lg bg-green-50 border border-green-200 p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-green-700">
                      <i className="fas fa-check-circle" /> WhatsApp is connected
                    </div>
                    <div className="mt-2 text-xs text-green-700/80 font-mono">
                      Phone Number ID: {config.phoneNumberId || "—"}
                    </div>
                  </div>

                  {/* Template card */}
                  <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-4">
                    <label className="block text-sm font-semibold text-blue-800 mb-2">
                      <i className="fas fa-file-alt mr-1.5 text-blue-500" /> Approved Message Template
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
                      <input value={templateName} onChange={(e) => setTemplateName(e.target.value)}
                        placeholder="e.g. offer_broadcast"
                        className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
                      <input value={templateLang} onChange={(e) => setTemplateLang(e.target.value)}
                        placeholder="en"
                        className="w-full sm:w-24 px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <p className="text-xs text-gray-400">
                        Must match a template approved in your WhatsApp Manager.
                      </p>
                      <button onClick={saveTemplate} disabled={working}
                        className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-white text-xs font-semibold shadow transition ${working ? "bg-gray-400" : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"}`}>
                        {working ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                        Save
                      </button>
                    </div>
                  </div>

                  <div className="pt-1">
                    <button onClick={disconnect} disabled={working}
                      className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-red-300 text-red-600 hover:bg-red-50 text-xs font-semibold disabled:opacity-50 transition">
                      <i className="fas fa-unlink" /> Disconnect
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    Link your WhatsApp Business number to send offers via WhatsApp.
                    You'll be guided through Meta's secure signup.
                  </p>

                  {/* How it works — jashbill styled steps */}
                  <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-4">
                    <p className="text-xs font-semibold text-blue-800 mb-2">How it works</p>
                    <ol className="space-y-2 text-xs text-gray-600">
                      <li className="flex items-start gap-2">
                        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-indigo-500 text-white text-[10px] font-bold">1</span>
                        Click Connect WhatsApp and sign in with Meta.
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-indigo-500 text-white text-[10px] font-bold">2</span>
                        Select your WhatsApp Business number.
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-indigo-500 text-white text-[10px] font-bold">3</span>
                        Add an approved template and start sending offers.
                      </li>
                    </ol>
                  </div>

                  <button onClick={launchSignup} disabled={!sdkReady || working}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-semibold shadow transition ${!sdkReady || working ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-emerald-600 hover:to-green-700"}`}>
                    {working ? <i className="fas fa-spinner fa-spin" /> : <i className="fab fa-whatsapp" />}
                    Connect WhatsApp
                  </button>
                  {!sdkReady && (
                    <p className="text-xs text-gray-400">Preparing secure signup…</p>
                  )}
                </>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppConnect;
